import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Users,
  CheckCircle,
  Clock,
  ArrowLeft,
  ArrowRight,
  X,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { payrollService } from "../../services/payrollService";
import { employeeService } from "../../services/employeeService";
import { getToken } from "../../utils/tokenUtils";
import Notification from "../../components/UI/Notification";
import Modal from "../../components/UI/Modal";

const GeneratePayroll = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    employeeSelectionType: "all", // "all" or "specific"
    selectedEmployeeIds: [],
    forceRegenerate: false,
    sendPayslips: false,
  });

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  // Fetch active employees
  const fetchEmployees = useCallback(async () => {
    setLoadingEmployees(true);
    try {
      const response = await employeeService.getEmployees({ status: "active" });
      const employeeList = response.data?.data || response.data || [];
      setEmployees(employeeList);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setNotification({
        type: "error",
        message: "Failed to fetch employees",
      });
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  useEffect(() => {
    if (currentStep === 2 && employees.length === 0) {
      fetchEmployees();
    }
  }, [currentStep, fetchEmployees, employees.length]);

  // Get employees to display in preview
  const getPreviewEmployees = () => {
    if (formData.employeeSelectionType === "all") {
      return employees.slice(0, 10); // Show first 10 for preview
    }
    return employees.filter((emp) =>
      formData.selectedEmployeeIds.includes(
        emp.employee_id || emp.employment_id || emp.id
      )
    );
  };

  // Get total employee count
  const getEmployeeCount = () => {
    if (formData.employeeSelectionType === "all") {
      return employees.length;
    }
    return formData.selectedEmployeeIds.length;
  };

  // Handle step navigation
  const handleNext = () => {
    if (currentStep === 1) {
      // Validate period selection
      if (!formData.year || !formData.month) {
        setNotification({
          type: "error",
          message: "Please select both year and month",
        });
        return;
      }
    } else if (currentStep === 2) {
      // Validate employee selection
      if (
        formData.employeeSelectionType === "specific" &&
        formData.selectedEmployeeIds.length === 0
      ) {
        setNotification({
          type: "error",
          message: "Please select at least one employee",
        });
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle employee selection
  const handleEmployeeToggle = (employeeId) => {
    const id = employeeId;
    setFormData((prev) => {
      const newIds = prev.selectedEmployeeIds.includes(id)
        ? prev.selectedEmployeeIds.filter((eid) => eid !== id)
        : [...prev.selectedEmployeeIds, id];
      return { ...prev, selectedEmployeeIds: newIds };
    });
  };

  const handleSelectAllEmployees = () => {
    if (formData.selectedEmployeeIds.length === employees.length) {
      setFormData((prev) => ({ ...prev, selectedEmployeeIds: [] }));
    } else {
      const allIds = employees.map(
        (emp) => emp.employee_id || emp.employment_id || emp.id
      );
      setFormData((prev) => ({ ...prev, selectedEmployeeIds: allIds }));
    }
  };

  // Handle payroll generation
  const handleGeneratePayroll = async () => {
    setLoading(true);
    try {
      const payload = {
        year: formData.year,
        month: formData.month,
        force_regenerate: formData.forceRegenerate,
        send_payslips: formData.sendPayslips,
      };

      // Add employee IDs only if specific selection is made
      if (
        formData.employeeSelectionType === "specific" &&
        formData.selectedEmployeeIds.length > 0
      ) {
        payload.employee_ids = formData.selectedEmployeeIds;
      }

      // Try bulk-generate endpoint first (as used in existing code)
      const token = getToken();
      const response = await fetch(
        "https://hrmspayroll.prepstripe.com/api/payroll/bulk-generate",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        setGenerationResult({
          success: true,
          successful: result.data?.successful || result.successful || getEmployeeCount(),
          failed: result.data?.failed || result.failed || 0,
          message: result.message || "Payroll generated successfully",
        });
        setShowSuccessModal(true);
      } else {
        throw new Error(result.message || "Failed to generate payroll");
      }
    } catch (error) {
      console.error("Error generating payroll:", error);
      setNotification({
        type: "error",
        message: error.message || "Failed to generate payroll",
      });
    } finally {
      setLoading(false);
    }
  };

  // Step Indicator Component
  const StepIndicator = () => {
    const steps = [
      { number: 1, label: "Period Selection", icon: Calendar },
      { number: 2, label: "Employee Selection", icon: Users },
      { number: 3, label: "Review & Confirm", icon: CheckCircle },
    ];

    return (
      <div className="flex items-center justify-center mb-8">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.number;
          const isCompleted = currentStep > step.number;
          const isPending = currentStep < step.number;

          return (
            <React.Fragment key={step.number}>
              <div className="flex items-center">
                <div
                  className={`flex flex-col items-center ${
                    isActive ? "text-blue-600" : isCompleted ? "text-green-600" : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                      isActive
                        ? "bg-blue-600 text-white"
                        : isCompleted
                        ? "bg-green-500 text-white"
                        : "bg-gray-300 text-gray-500"
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 size={24} />
                    ) : (
                      <Icon size={24} />
                    )}
                  </div>
                  <span
                    className={`mt-2 text-sm font-medium ${
                      isActive
                        ? "text-blue-600"
                        : isCompleted
                        ? "text-green-600"
                        : "text-gray-400"
                    }`}
                  >
                    Step {step.number} {step.label}
                  </span>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`h-0.5 w-16 mx-4 transition-all ${
                    isCompleted ? "bg-green-500" : isActive ? "bg-blue-600" : "bg-gray-300"
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Generate Payroll
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Create payroll for selected period and employees
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search employees, payrolls..."
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          
          
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      <StepIndicator />

      {/* Step Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
        {/* Step 1: Period Selection */}
        {currentStep === 1 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Select Payroll Period
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Choose the year and month for which you want to generate payroll
            </p>

            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Year
                </label>
                <select
                  value={formData.year}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      year: parseInt(e.target.value),
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Month
                </label>
                <select
                  value={formData.month}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      month: parseInt(e.target.value),
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                >
                  {months.map((month, index) => (
                    <option key={index} value={index + 1}>
                      {month}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                If payroll already exists for this period, you can choose to
                regenerate it in the final step.
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Employee Selection */}
        {currentStep === 2 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Select Employees
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Choose which employees to include in this payroll run
            </p>

            <div className="space-y-4">
              {/* All Active Employees Option */}
              <div
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  formData.employeeSelectionType === "all"
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                }`}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    employeeSelectionType: "all",
                  }))
                }
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      formData.employeeSelectionType === "all"
                        ? "border-blue-500 bg-blue-500"
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                  >
                    {formData.employeeSelectionType === "all" && (
                      <div className="w-3 h-3 rounded-full bg-white"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      All Active Employees
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Generate payroll for all {employees.length || 0} active employees
                    </p>
                  </div>
                </div>
              </div>

              {/* Select Specific Employees Option */}
              <div
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  formData.employeeSelectionType === "specific"
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                }`}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    employeeSelectionType: "specific",
                  }))
                }
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      formData.employeeSelectionType === "specific"
                        ? "border-blue-500 bg-blue-500"
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                  >
                    {formData.employeeSelectionType === "specific" && (
                      <div className="w-3 h-3 rounded-full bg-white"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      Select Specific Employees
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Choose individual employees for this payroll run
                    </p>
                  </div>
                </div>
              </div>

              {/* Employee List (when specific selection is chosen) */}
              {formData.employeeSelectionType === "specific" && (
                <div className="mt-6 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  {loadingEmployees ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          Select Employees
                        </h3>
                        <button
                          onClick={handleSelectAllEmployees}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          {formData.selectedEmployeeIds.length === employees.length
                            ? "Deselect All"
                            : "Select All"}
                        </button>
                      </div>
                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {employees.map((employee) => {
                          const empId =
                            employee.employee_id ||
                            employee.employment_id ||
                            employee.id;
                          const isSelected = formData.selectedEmployeeIds.includes(
                            empId
                          );
                          return (
                            <label
                              key={empId}
                              className="flex items-center gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleEmployeeToggle(empId)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {employee.first_name} {employee.last_name}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {employee.employee_id || employee.employment_id || empId} •{" "}
                                  {employee.department_name || employee.department || "N/A"}
                                </p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      {formData.selectedEmployeeIds.length > 0 && (
                        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                          {formData.selectedEmployeeIds.length} employee(s) selected
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Review & Confirm */}
        {currentStep === 3 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Review & Confirm
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Review your selections before generating payroll
            </p>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Period</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {months[formData.month - 1]} {formData.year}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Employees</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {getEmployeeCount()} employees
                </p>
              </div>
            </div>

            {/* Options */}
            <div className="space-y-4 mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.forceRegenerate}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      forceRegenerate: e.target.checked,
                    }))
                  }
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700 dark:text-gray-300">
                  Force regenerate if payroll already exists for this period
                </span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.sendPayslips}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sendPayslips: e.target.checked,
                    }))
                  }
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700 dark:text-gray-300">
                  Send payslips to employees after generation
                </span>
              </label>
            </div>

            {/* Employee Preview */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white">Preview</h3>
              </div>
              <div className="max-h-64 overflow-y-auto p-4">
                {loadingEmployees ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.employeeSelectionType === "all" && employees.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                        Loading employees...
                      </p>
                    ) : (
                      getPreviewEmployees().map((employee) => {
                        const empId =
                          employee.employee_id ||
                          employee.employment_id ||
                          employee.id;
                        return (
                          <div
                            key={empId}
                            className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {employee.first_name} {employee.last_name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {employee.department_name || employee.department || "N/A"}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between mt-6">
        <button
          onClick={handleBack}
          disabled={currentStep === 1}
          className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors ${
            currentStep === 1
              ? "bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed"
              : "bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          }`}
        >
          <ArrowLeft size={18} />
          Back
        </button>

        {currentStep < 3 ? (
          <button
            onClick={handleNext}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Next
            <ArrowRight size={18} />
          </button>
        ) : (
          <button
            onClick={handleGeneratePayroll}
            disabled={loading}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            } text-white`}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Generating...
              </>
            ) : (
              "Generate Payroll"
            )}
          </button>
        )}
      </div>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onRequestClose={() => setShowSuccessModal(false)}
        title=""
      >
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircle2 size={32} className="text-white" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Payroll Generated Successfully!
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Payroll has been generated for {generationResult?.successful || 0} employees for{" "}
            {months[formData.month - 1]} {formData.year}.
          </p>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                {generationResult?.successful || 0}
              </p>
              <p className="text-sm text-green-600 dark:text-green-400 mt-1">Successful</p>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                {generationResult?.failed || 0}
              </p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">Failed</p>
            </div>
          </div>

          <button
            onClick={() => {
              setShowSuccessModal(false);
              navigate("/payroll/processed-records");
            }}
            className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            View Payroll List
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default GeneratePayroll;
